// AskClaraLiveField — TopNav input that opens the full-page Clara Live
// consultant at /consultant, seeding the conversation with the user's
// question via the ?q= URL param.
//
// Behavior:
//   * Non-premium users: the field doesn't render (matches the
//     ConsultantBubble's existing premium gate — keeping all consultant
//     entry points consistent).
//   * Premium users:
//       - Typing + Enter → navigates to /consultant?q=<encoded text>.
//         The page reads the param on mount, starts a new conversation,
//         sends the message, and scrubs the URL so refresh doesn't replay.
//       - Clicking the field with no text → navigates to /consultant
//         without a question (empty chat surface, suggestions visible).
//
// Note: the floating ConsultantBubble (bottom-right) is the ONLY path
// that still opens the side drawer. Every other consultant entry point
// (this field, ⌘K palette, hub tile, sidebar) routes to /consultant.
//
// Visual: amber/primary-colored pill, distinct from the gray ⌘K palette
// button, so it reads as "AI chat" not "tool search" at a glance.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConsultant } from "@/contexts/ConsultantContext";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { canAccessTier } from "@/data/tierAccess";

export function AskClaraLiveField() {
  const navigate = useNavigate();
  const { isPending } = useConsultant();
  const { tier, loading } = useSubscriptionTier();
  const [text, setText] = useState("");

  // Consultant is Choice-only. Free + Select subscribers don't see any
  // consultant entry points — they get a quiet sidebar + palette UX
  // without an upsell nag.
  if (loading) return null;
  if (!tier || !canAccessTier(tier, "choice")) return null;

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) {
      navigate("/consultant");
      return;
    }
    setText("");
    // Pass the question via ?q=. ConsultantPage's mount effect picks it
    // up, starts a new conversation, sends the message, clears the URL.
    navigate(`/consultant?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "hidden md:flex items-center gap-2 h-8 pl-3 pr-1 rounded-full min-w-[240px] max-w-[380px] flex-1 transition-colors",
      )}
      style={{
        background: "hsl(var(--primary) / 0.10)",
        border: "1px solid hsl(var(--primary) / 0.45)",
        boxShadow: "0 0 0 0 hsl(var(--primary) / 0)",
      }}
      title="Ask the RaisedBeef Cattle Consultant — press Enter to send"
    >
      <Sparkles
        className="h-3.5 w-3.5 shrink-0"
        style={{ color: "hsl(var(--primary))" }}
      />
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Ask the Cattle Consultant…"
        aria-label="Ask the RaisedBeef Cattle Consultant"
        disabled={isPending}
        className="flex-1 bg-transparent outline-none border-none text-xs placeholder:text-foreground/50"
        style={{ color: "hsl(var(--foreground))" }}
        onFocus={() => {
          // Opening on focus is too aggressive; we only open on submit or
          // on click of the send button.
        }}
      />
      <button
        type="submit"
        disabled={isPending}
        aria-label="Send to Clara Live"
        className={cn(
          "inline-flex items-center justify-center h-6 w-6 rounded-full transition-opacity",
          "disabled:opacity-40",
        )}
        style={{
          background: "hsl(var(--primary))",
          color: "hsl(var(--background))",
        }}
      >
        <ArrowRight className="h-3 w-3" />
      </button>
    </form>
  );
}
